import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Paper, Typography } from '@esgian/esgianui';
import { DrillingActivitySummary } from '@components';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { PeriodicOverviewWithDetailsChart } from '@components/Charts';
import { useDisplayUnit, useSeriesBuilder } from '@hooks';
import { DATE_FORMAT, TIMESTAMP, UTC_TIMESTAMP } from '@constants/index';
import LogDrawer from '@components/Drawers/LogDrawer/LogDrawer';
import {
  getMainPageSlice,
  setChartSelectedDate
} from '@store/features/filters/MainPageSlice/MainPageSlice';
import { useTimezone } from '@hooks/useTimezone';
import { useMainPageQueries } from '@hooks/usePageQueries/useMainPageQueries';
import { useDispatch, useSelector } from 'react-redux';
import { getValue } from '@helpers';
import { findDrillingEvent } from '@api/helpers';

const getDetailsChartTitle = (unit) => {
  switch (unit) {
    case 'Energy':
      return (
        <Typography variant="body2">
          Detailed drilling equipment energy consumption in selection
        </Typography>
      );
    case 'CO2':
      return (
        <Typography variant="body2">
          Detailed drilling equipment CO<sub>2</sub>e emissions in selection
        </Typography>
      );
    case 'Fuel':
      return (
        <Typography variant="body2">
          Detailed drilling equipment fuel consumption in selection
        </Typography>
      );
    case 'NOx':
      return (
        <Typography variant="body2">
          Detailed drilling equipment NO<sub>x</sub> emissions in selection
        </Typography>
      );
  }
};

/**
 * Function that converts the api data into a day/hour rollup.
 * */
const sumDataByUnit = (data, viewByUnit, displayUnit, selectedTimeZone, start, end) => {
  let activities = [];
  let cat = [];
  let dataTimes = {};
  let startDate = moment(start);
  let endDate = moment(end);
  while (startDate.isSameOrBefore(endDate)) {
    let dayKey = startDate.clone().format(UTC_TIMESTAMP);
    cat.push(dayKey);
    dataTimes[dayKey] = 0;
    startDate.add(1, viewByUnit);
  }

  if (!data?.drillingActivities)
    return { data: [], categories: [] };
  
  data.drillingActivities.forEach(({ activity, chartConfig, events }) => {
    let data = { ...dataTimes };

    var drillingSignals = findDrillingEvent(events, 'Drilling', displayUnit);
    if (!drillingSignals || !drillingSignals.timeSeriesValues) {
      activities.push({ activity: activity, data: data, chartConfig: chartConfig });
      return;
    }

    let measurements = drillingSignals.timeSeriesValues;

    Object.keys(measurements).forEach((key) => {
      let dayKey = moment.parseZone(key).startOf(viewByUnit).format(UTC_TIMESTAMP);
      if (data[dayKey] !== undefined) {
        data[dayKey] += getValue(measurements[key]);
      }
    });
    let tempName = activity;
    if (tempName === 'Uncategorized') {
      tempName = 'Activity data missing';
    }

    activities.push({ activity: tempName, data: data, chartConfig: chartConfig });
  });

  return { data: activities, categories: cat };
};

function DrillingActivitiesSection({ displayUnit }) {
  const dispatch = useDispatch();
  const [logInfo, setLogInfo] = useState(null);
  const { getUnitTitle } = useDisplayUnit();
  const { getCanvasTimeSeriesData } = useSeriesBuilder();
  const { startDate, endDate, chartSelectedDate } = useSelector(getMainPageSlice);
  const { rigDrillingActivityQuery } = useMainPageQueries();
  const { selectedTimeZone } = useTimezone();

  const getSeriesAndUnit = (data) => {
    if (!data?.length) return {};
    const maxValue = Math.max(...data.flatMap((obj) => Object.values(obj.data)));
    const { converted, title } = getUnitTitle(maxValue);
    const orderMap = {};
    const series = data
      .map(({ activity, chartConfig, data }) => {
        const { defaultArray, convertedArray } = getCanvasTimeSeriesData(data, displayUnit);
        let orderKey = activity.toLowerCase();
        orderMap[orderKey] = chartConfig.order;
        return {
          type: 'bar',
          backgroundColor: chartConfig.bgColour,
          stack: 1,
          borderSkipped: true,
          label: activity,
          data: converted ? convertedArray : defaultArray
        };
      })
      .sort((a, b) => {
        const indexA = orderMap[a.label.toLowerCase()];
        const indexB = orderMap[b.label.toLowerCase()];
        return indexA - indexB;
      });

    return { series, unit: title };
  };

  const {
    primarySeries = [],
    primUnit = '',
    categories
  } = useMemo(() => {
    if (!rigDrillingActivityQuery.data) return {};
    const { data, categories } = sumDataByUnit(
      rigDrillingActivityQuery.data,
      'day',
      displayUnit,
      selectedTimeZone,
      moment(startDate.split('T')[0]).format(DATE_FORMAT),
      moment(endDate.split('T')[0]).format(DATE_FORMAT)
    );
    const filteredData = data.filter((item) => {
      return !Object.values(item.data).every((value) => value === 0);
    });
    const { series, unit } = getSeriesAndUnit(filteredData);

    // Only return the calculated series and unit here
    return { primarySeries: series, primUnit: unit, categories };
  }, [rigDrillingActivityQuery.data, displayUnit]);

  // Use an effect to dispatch setChartSelectedDate safely
  useEffect(() => {
    if (!chartSelectedDate && categories?.length > 0) {
      dispatch(
        setChartSelectedDate({
          date: moment.utc(categories[0]).format(UTC_TIMESTAMP),
          index: 0,
          isLast: false
        })
      );
    }
  }, [chartSelectedDate, categories, dispatch]);

  const { secondarySeries = [], secondaryUnit = '' } = useMemo(() => {
    if (!chartSelectedDate) return {};
    let sDate = moment(chartSelectedDate.date).startOf('day');
    let eDate = moment(chartSelectedDate.date).endOf('day');
    if (chartSelectedDate.index >= 1) {
      sDate = sDate.subtract(1, 'days');
    }
    if (!chartSelectedDate.isLast) {
      eDate = eDate.add(1, 'days');
    }
    const timezoneMoment = moment.tz(eDate, selectedTimeZone); // Ensure timezone alignment
    const now = moment.tz(selectedTimeZone); // Current time in the same timezone

    if (timezoneMoment.isAfter(now)) {
      eDate = now; // Adjust `eDate` to current time if it's in the future
    }
    eDate = moment(eDate.format(TIMESTAMP));

    const { data, categories } = sumDataByUnit(
      rigDrillingActivityQuery.data,
      'hour',
      displayUnit,
      selectedTimeZone,
      sDate,
      eDate
    );
    let filteredData = data.filter((item) => {
      return !Object.values(item.data).every((value) => value === 0);
    });
    const { series: series, unit } = getSeriesAndUnit(filteredData);
    
    return { secondarySeries: series, secondaryUnit: unit, secondaryCategories: categories };
  }, [rigDrillingActivityQuery.data, displayUnit, chartSelectedDate]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={3.5} id="drilling-activity-summary-section">
        <DrillingActivitySummary
          drillingData={rigDrillingActivityQuery.data}
          loading={rigDrillingActivityQuery.isFetching}
        />
      </Grid>
      <Grid item xs={8.5} id="drilling-activity-overview-section">
        <Paper sx={{ p: 2 }}>
          <PeriodicOverviewWithDetailsChart
            secondaryStacked
            enableBarHover
            disableHoverLinePlugin
            secondaryUnit={secondaryUnit}
            secondarySeries={secondarySeries}
            secondaryChartStacked
            primaryUnit={primUnit}
            loading={rigDrillingActivityQuery.isFetching}
            selectedView={'hour'}
            secondaryChartType={'bar'}
            selectedColumn={chartSelectedDate}
            setSelectedColumn={(v) => dispatch(setChartSelectedDate(v))}
            handleLogClick={setLogInfo}
            primarySeries={primarySeries}
            title="Activity overview"
            titleTooltipText={`Drilling equipment consumption or emissions per day for each activity in the selected time period.
              Clicking on one of the bars will reveal the details per hour for the day before, selected day and the next day in the Activity details graph below.`}
            detailsTitle="Activity details"
            detailsTitleTooltipText={`Total consumption or emissions per hour for each activity.
              Based on the day selected in the Periodic overview graph above, it shows the day before, the selected day and the next day's hourly activities.
              Clicking on view log will provide details about the activities in that particular hour.`}
            detailsSubTitle={getDetailsChartTitle(displayUnit)}
            id="drilling-activity-details-chart"
            displayReused={false}
          />
          <LogDrawer handleClose={() => setLogInfo(null)} open={!!logInfo} logInfo={logInfo} />
        </Paper>
      </Grid>
    </Grid>
  );
}

DrillingActivitiesSection.propTypes = {
  displayUnit: PropTypes.string
};

DrillingActivitiesSection.defaultProps = {};

export default DrillingActivitiesSection;
